import { addToast, Modal } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdmissionScope, Offer, OfferListFormValues } from '../../types';
import { formatFirstInstallmentRule } from '../../utils';
import { updateAdmissionAction } from '../actions';
import { addOfferAction } from '../managementActions';
import AdmissionProcessOfferForm from './AdmissionProcessOfferForm';

interface Props {
  toggle: () => void;
  offer?: Offer;
  availableOffers: any[];
  processFirstInstallmentRuleId?: number;
  refresh: () => void;
}

export default function OfferModal({
  toggle,
  offer,
  availableOffers,
  processFirstInstallmentRuleId,
  refresh,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const { id } = useParams<{ id: string }>();
  const isEdit = !!offer;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(
    async (data: OfferListFormValues & { offerId: any }) => {
      setIsSubmitting(true);
      const {
        startDate,
        duration,
        enrollmentType,
        maxInstallments,
        quota,
        offerId,
        ...rest
      } = data;

      const firstInstallmentRuleValue = formatFirstInstallmentRule(
        rest,
        isEdit ? offer?.firstInstallmentRule?.id : undefined,
      );

      if (!firstInstallmentRuleValue) {
        setIsSubmitting(false);
        return;
      }

      const commonData = {
        admissionScopes: enrollmentType?.value
          ? (enrollmentType.value.toString().split(',') as AdmissionScope[])
          : null,
        maxInstallments: maxInstallments?.value ? +maxInstallments.value : null,
        quota: quota ? +quota : null,
        firstInstallmentRuleValue,
        tuitionProcessFirstInstallmentRuleId:
          processFirstInstallmentRuleId ?? undefined,
      };

      let result;
      if (isEdit && offer) {
        result = await updateAdmissionAction({
          admissionId: offer.admissionId,
          data: commonData,
        });
      } else {
        if (!id || !offerId) {
          setIsSubmitting(false);
          return;
        }

        result = await addOfferAction({
          id: +id,
          data: {
            offerId: offerId.value,
            ...commonData,
          },
        });
      }

      if (result) {
        addToast({
          text: isEdit
            ? t('processList.modalForm.success')
            : t('offersTable.addSuccess'),
          color: 'success',
        });
      } else {
        addToast({
          text: isEdit
            ? t('processList.modalForm.error')
            : t('offersTable.addError'),
          color: 'danger',
        });
      }
      setIsSubmitting(false);
      refresh();
      toggle();
    },
    [t, isEdit, offer, refresh, toggle, processFirstInstallmentRuleId, id],
  );

  return (
    <Modal isOpen toggle={toggle} size="lg" closeOnBackdrop={false}>
      <AdmissionProcessOfferForm
        offer={offer}
        availableOffers={availableOffers}
        onSubmit={onSubmit}
        onCancel={toggle}
        isSubmitting={isSubmitting}
      />
    </Modal>
  );
}
